import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Target, BookOpen, Dumbbell, Palette, Music, Code } from "lucide-react";

interface StepTwoProps {
  onNext: (goal: { title: string; category: string }) => void;
  onBack: () => void;
}

const goalTemplates = [
  {
    title: "Read 5 Books",
    category: "Education",
    icon: BookOpen,
    description: "Build a reading habit"
  },
  {
    title: "Exercise 3 Times a Week",
    category: "Health",
    icon: Dumbbell,
    description: "Get moving and stay healthy"
  },
  {
    title: "Learn to Draw",
    category: "Creative",
    icon: Palette,
    description: "Express your creativity"
  },
  {
    title: "Practice an Instrument",
    category: "Music",
    icon: Music,
    description: "Make beautiful music"
  },
  {
    title: "Learn to Code",
    category: "Technology",
    icon: Code,
    description: "Build cool projects"
  },
  {
    title: "I'll Create My Own",
    category: "Personal",
    icon: Target,
    description: "Start with your own goal"
  },
];

export function StepTwo({ onNext, onBack }: StepTwoProps) {
  const [selectedGoal, setSelectedGoal] = useState(goalTemplates[0]);

  return (
    <div className="space-y-6 text-center">
      <div className="space-y-3">
        <h2 className="text-3xl font-bold">Pick Your First Goal! 🎯</h2>
        <p className="text-muted-foreground text-lg">
          Choose a goal to get started, or create your own later
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-left">
        {goalTemplates.map((goal) => {
          const Icon = goal.icon;
          return (
            <button
              type="button"
              key={goal.title}
              onClick={() => setSelectedGoal(goal)}
              className={`
                p-4 rounded-xl border-2 transition-all hover:scale-105 text-left
                ${selectedGoal.title === goal.title
                  ? "border-[#9b87f5] bg-gradient-to-br from-[#F97316]/10 to-[#D946EF]/10 scale-105 shadow-lg"
                  : "border-border hover:border-muted-foreground"
                }
              `}
            >
              <div className="flex items-start gap-3">
                <div className={`
                  w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0
                  ${selectedGoal.title === goal.title 
                    ? "bg-gradient-to-br from-[#F97316] to-[#D946EF]" 
                    : "bg-muted"
                  }
                `}>
                  <Icon className={`w-6 h-6 ${selectedGoal.title === goal.title ? "text-white" : "text-muted-foreground"}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-base mb-1">{goal.title}</h3>
                  <p className="text-sm text-muted-foreground">{goal.description}</p>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <div className="flex gap-3">
        <Button 
          type="button"
          variant="outline" 
          size="lg" 
          className="flex-1"
          onClick={onBack}
        >
          ← Back
        </Button>
        <Button 
          type="button"
          size="lg" 
          className="flex-1 bg-gradient-to-r from-[#F97316] to-[#D946EF] hover:opacity-90"
          onClick={() => onNext({ title: selectedGoal.title, category: selectedGoal.category })}
        >
          Next Step →
        </Button>
      </div>
    </div>
  );
}
